import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { pickSkeleton, SMS_VARIANT_KEY } from '~/components/NotificationPreview/NotificationEmailPreview/NotificationEmailPreview.utils';
import { useAcceptNotificationRewrite } from '~/hooks/reactQuery/mutations/useAcceptNotificationRewrite';
import { useGenerateNotificationRewrite } from '~/hooks/reactQuery/mutations/useGenerateNotificationRewrite';
import { NOTIFICATION_REWRITE_PRESETS } from '~/hooks/reactQuery/mutations/useGenerateNotificationRewrite.types';
import { useRejectNotificationRewrite } from '~/hooks/reactQuery/mutations/useRejectNotificationRewrite';
import { useUndoNotificationRewrite } from '~/hooks/reactQuery/mutations/useUndoNotificationRewrite';
import { useLocaleKeys } from '~/i18n';
import { HelpMeWriteOutputRenderer } from '~/pages/NotificationSettings/components/NotificationEditModal/components/HelpMeWriteOutputRenderer/HelpMeWriteOutputRenderer';
import HelpMeWriteDrawer from '../HelpMeWriteDrawer';
import { adaptNotificationRewriteResponse, enrichDynamicFields } from './NotificationHelpMeWriteDrawer.utils';
const NOTIFICATION_BI_EVENT_IDS = {
    presetClick: {
        formalize: 17050,
        polish: 17051,
        soften: 17052,
        shorten: 17053
    },
    submitPrompt: 17054,
    undoClick: 17055,
    insertClick: 17056,
    replaceClick: 17056,
    closeClick: 17057
};
const renderNotificationOutput = (props)=>/*#__PURE__*/ _jsx(HelpMeWriteOutputRenderer, {
        value: props.value
    });
const toRewritePrompt = (prompt)=>prompt.kind === 'freeText' ? {
        kind: 'freeText',
        text: prompt.text
    } : {
        kind: 'preset',
        preset: prompt.preset
    };
export const NotificationHelpMeWriteDrawer = (param)=>{
    let { open, onClose, drawerWidth, notificationName, variantKey, recipientParty, activeVariant, ctaButtonText, onAccept } = param;
    const { t: drawerTexts } = useLocaleKeys('notificationHelpMeWrite.drawer');
    const { t: presetTexts } = useLocaleKeys('notificationHelpMeWrite.presets');
    const { t: promptSuggestionTexts } = useLocaleKeys('notificationHelpMeWrite.promptSuggestions');
    const { t: helpMeWriteTexts } = useLocaleKeys('helpMeWrite');
    const { t: suggestedTexts } = useLocaleKeys('helpMeWrite.suggestedDescription');
    const { t: textFieldTexts } = useLocaleKeys('helpMeWrite.textField');
    const { startConversation, sendMessage } = useGenerateNotificationRewrite();
    const { undoNotificationRewrite } = useUndoNotificationRewrite();
    const { acceptNotificationRewrite } = useAcceptNotificationRewrite();
    const { rejectNotificationRewrite } = useRejectNotificationRewrite();
    const isSms = variantKey.toLowerCase() === SMS_VARIANT_KEY;
    const dynamicFields = useMemo(()=>enrichDynamicFields(activeVariant.templateVariables), [
        activeVariant.templateVariables
    ]);
    const skeleton = useMemo(()=>pickSkeleton(notificationName), [
        notificationName
    ]);
    const presets = useMemo(()=>NOTIFICATION_REWRITE_PRESETS.map((preset)=>({
                value: preset,
                label: presetTexts[preset](),
                biName: preset
            })), [
        presetTexts
    ]);
    const suggestions = useMemo(()=>[
            promptSuggestionTexts.p1(),
            promptSuggestionTexts.p2(),
            promptSuggestionTexts.p3(),
            promptSuggestionTexts.p4(),
            promptSuggestionTexts.p5(),
            promptSuggestionTexts.p6(),
            promptSuggestionTexts.p7(),
            promptSuggestionTexts.p8()
        ], [
        promptSuggestionTexts
    ]);
    const texts = useMemo(()=>({
            headerTitle: drawerTexts.title(),
            headerBeta: helpMeWriteTexts.beta(),
            promptQuestion: drawerTexts.promptLabel(),
            insertLabel: drawerTexts.insertButtonLabel(),
            insertedLabel: suggestedTexts.inserted(),
            replaceLabel: drawerTexts.insertButtonLabel(),
            replacedLabel: suggestedTexts.replaced(),
            undoLabel: suggestedTexts.undo(),
            placeholder: drawerTexts.promptPlaceholderWhileTyping(),
            placeholderAfterStart: textFieldTexts.placeholder(),
            disclaimer: textFieldTexts.disclaimer(),
            errorMessage: drawerTexts.errorMessage()
        }), [
        drawerTexts,
        helpMeWriteTexts,
        suggestedTexts,
        textFieldTexts
    ]);
    const mutations = useMemo(()=>{
        const buildStartRequest = (prompt)=>isSms ? {
                channel: 'sms',
                notificationType: notificationName,
                variantKey,
                recipientParty,
                dynamicFields,
                skeleton,
                message: activeVariant.message,
                prompt
            } : {
                channel: 'email',
                notificationType: notificationName,
                variantKey,
                recipientParty,
                dynamicFields,
                skeleton,
                subject: activeVariant.subject,
                message: activeVariant.message,
                ctaButtonText,
                prompt
            };
        return {
            onMessage: async (conversationId, prompt)=>{
                const rewritePrompt = toRewritePrompt(prompt);
                if (conversationId === '') {
                    const response = await startConversation(buildStartRequest(rewritePrompt));
                    return adaptNotificationRewriteResponse(response);
                }
                const continueRequest = {
                    prompt: rewritePrompt
                };
                const response = await sendMessage(conversationId, continueRequest);
                return adaptNotificationRewriteResponse(response);
            },
            onUndo: async (conversationId)=>{
                const response = await undoNotificationRewrite(conversationId);
                return adaptNotificationRewriteResponse(response);
            },
            onAccept: async (conversationId, output)=>{
                await onAccept(output);
                await acceptNotificationRewrite(conversationId);
            },
            onReject: async (conversationId)=>{
                await rejectNotificationRewrite(conversationId);
            }
        };
    }, [
        isSms,
        notificationName,
        variantKey,
        recipientParty,
        dynamicFields,
        skeleton,
        activeVariant.message,
        activeVariant.subject,
        ctaButtonText,
        startConversation,
        sendMessage,
        undoNotificationRewrite,
        acceptNotificationRewrite,
        rejectNotificationRewrite,
        onAccept
    ]);
    const hasExistingContent = isSms ? activeVariant.message.trim() !== '' : activeVariant.subject.trim() !== '' || activeVariant.message.trim() !== '';
    return /*#__PURE__*/ _jsx(HelpMeWriteDrawer, {
        open: open,
        onClose: onClose,
        drawerWidth: drawerWidth,
        presets: presets,
        suggestions: suggestions,
        mutations: mutations,
        outputRenderer: renderNotificationOutput,
        biEventIds: NOTIFICATION_BI_EVENT_IDS,
        texts: texts,
        hasExistingContent: hasExistingContent
    });
};
